import React, { useState, useEffect } from 'react';
import { integrationService } from '../services/integrationService';

const INTEGRATION_NAMES = {
  ismartrecruit: 'iSmartRecruit',
  bullhorn: 'Bullhorn',
  recruitcrm: 'Recruit CRM'
};

const OBJECT_TYPES = [
  { value: 'candidates', label: 'Candidates' },
  { value: 'companies', label: 'Companies' },
  { value: 'contacts', label: 'Contacts' },
  { value: 'jobs', label: 'Jobs' },
  { value: 'placements', label: 'Placements' },
  { value: 'activities', label: 'Activities' }
];

const INCREMENTAL_FIELDS = [
  { value: 'updated_at', label: 'Updated At' },
  { value: 'modified_date', label: 'Modified Date' },
  { value: 'last_modified', label: 'Last Modified' }
];

export default function IntegrationConfig({ integrationType, onCancel, onSave }) {
  const [formData, setFormData] = useState({
    is_enabled: false,
    sync_frequency: 'manual',
    sync_time: '09:00',
    include_weekends: false,
    enabled_objects: [],
    incremental_enabled: true,
    incremental_field: 'updated_at',
    rate_limit_per_minute: 60,
    max_retries: 3,
    credentials: {}
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [credentialsMasked, setCredentialsMasked] = useState(false);

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationType]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const config = await integrationService.getIntegrationConfig(integrationType);
      
      if (config) {
        setFormData({
          is_enabled: config.is_enabled || false,
          sync_frequency: config.sync_frequency || 'manual',
          sync_time: config.sync_time || '09:00',
          include_weekends: config.include_weekends || false,
          enabled_objects: config.enabled_objects || [],
          incremental_enabled: config.incremental_enabled !== false,
          incremental_field: config.incremental_field || 'updated_at',
          rate_limit_per_minute: config.rate_limit_per_minute || 60,
          max_retries: config.max_retries || 3,
          credentials: {}
        });
        setCredentialsMasked(config.has_credentials || false);
      }
    } catch (err) {
      console.error('Failed to load config:', err);
      setError(err.message || 'Failed to load integration configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
    setSuccess('');
    setTestResult(null);
  };

  const handleObjectToggle = (objectType) => {
    setFormData(prev => ({
      ...prev,
      enabled_objects: prev.enabled_objects.includes(objectType)
        ? prev.enabled_objects.filter(o => o !== objectType)
        : [...prev.enabled_objects, objectType]
    }));
  };

  const handleCredentialChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      credentials: {
        ...prev.credentials,
        [key]: value
      }
    }));
    setCredentialsMasked(false);
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setError('');
      setTestResult(null);

      const credentials = getCredentialsForType();
      await integrationService.testConnection(integrationType, credentials);
      
      setTestResult({
        success: true,
        message: 'Connection test successful!'
      });
      setSuccess('Connection test passed');
    } catch (err) {
      setTestResult({
        success: false,
        message: err.message || 'Connection test failed'
      });
      setError(err.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const getCredentialsForType = () => {
    // Only include credentials if they have actual values (not empty strings)
    if (integrationType === 'ismartrecruit') {
      const apiKey = formData.credentials.api_key?.trim();
      // If credentials are masked and no new value provided, return empty object to keep existing
      if (credentialsMasked && !apiKey) {
        return {};
      }
      return apiKey ? { api_key: apiKey } : {};
    } else if (integrationType === 'recruitcrm') {
      const apiToken = formData.credentials.api_token?.trim();
      if (credentialsMasked && !apiToken) {
        return {};
      }
      return apiToken ? { api_token: apiToken } : {};
    } else if (integrationType === 'bullhorn') {
      // Bullhorn uses OAuth - credentials will be handled separately
      return formData.credentials;
    }
    return formData.credentials;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const config = {
        is_enabled: formData.is_enabled,
        sync_frequency: formData.sync_frequency,
        sync_time: formData.sync_frequency === 'daily' ? formData.sync_time : null,
        include_weekends: formData.include_weekends,
        enabled_objects: formData.enabled_objects,
        incremental_enabled: formData.incremental_enabled,
        incremental_field: formData.incremental_enabled ? formData.incremental_field : null,
        rate_limit_per_minute: parseInt(formData.rate_limit_per_minute),
        max_retries: parseInt(formData.max_retries),
        credentials: getCredentialsForType()
      };

      await integrationService.saveIntegrationConfig(integrationType, config);
      setSuccess('Configuration saved successfully!');
      setCredentialsMasked(true);
      
      if (onSave) {
        onSave();
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C6AE]"></div>
          <p className="mt-2 text-gray-500">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">
          Configure {INTEGRATION_NAMES[integrationType] || integrationType}
        </h2>
        <button
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          ← Back to Integrations
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 text-sm">{success}</p>
        </div>
      )}

      {testResult && (
        <div className={`mb-4 p-4 border rounded-lg ${
          testResult.success 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <p className={`text-sm ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
            {testResult.message}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <label className="text-sm font-medium text-gray-900">Enable Integration</label>
            <p className="text-xs text-gray-500 mt-1">Turn on this integration to start syncing data</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="is_enabled"
              checked={formData.is_enabled}
              onChange={handleChange}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#00C6AE]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00C6AE]"></div>
          </label>
        </div>

        {/* Credentials Section */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Credentials</h3>
          
          {integrationType === 'ismartrecruit' && (
            <div>
              <label htmlFor="api_key" className="block text-sm font-medium text-gray-700 mb-2">
                API Key
              </label>
              <div className="flex gap-2">
                <input
                  type="password"
                  id="api_key"
                  value={formData.credentials.api_key || ''}
                  onChange={(e) => handleCredentialChange('api_key', e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
                  placeholder={credentialsMasked ? "Enter new API key to update" : "Enter API key"}
                />
                {credentialsMasked && (
                  <button
                    type="button"
                    onClick={() => {
                      setCredentialsMasked(false);
                      setFormData(prev => ({
                        ...prev,
                        credentials: { ...prev.credentials, api_key: '' }
                      }));
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
              {credentialsMasked && formData.credentials.api_key === '' && (
                <p className="mt-1 text-xs text-gray-500">
                  Credentials are saved. Enter new credentials above to update, or leave empty to keep existing credentials.
                </p>
              )}
            </div>
          )}

          {integrationType === 'recruitcrm' && (
            <div>
              <label htmlFor="api_token" className="block text-sm font-medium text-gray-700 mb-2">
                API Token
              </label>
              <input
                type={credentialsMasked ? 'password' : 'text'}
                id="api_token"
                value={credentialsMasked ? '••••••••' : (formData.credentials.api_token || '')}
                onChange={(e) => handleCredentialChange('api_token', e.target.value)}
                disabled={credentialsMasked}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent disabled:bg-gray-50"
                placeholder="Enter API token"
              />
              {credentialsMasked && (
                <p className="mt-1 text-xs text-gray-500">
                  Credentials are saved. Enter new credentials to update.
                </p>
              )}
            </div>
          )}

          {integrationType === 'bullhorn' && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                Bullhorn uses OAuth 2.0 authentication. Click the button below to initiate the OAuth flow.
              </p>
              <button
                type="button"
                className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Initiate OAuth Flow
              </button>
            </div>
          )}

          <div className="mt-4">
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testing || credentialsMasked}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
          </div>
        </div>

        {/* Sync Settings */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sync Settings</h3>

          {/* Sync Frequency */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Sync Frequency</label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sync_frequency"
                  value="manual"
                  checked={formData.sync_frequency === 'manual'}
                  onChange={handleChange}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Manual</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sync_frequency"
                  value="daily"
                  checked={formData.sync_frequency === 'daily'}
                  onChange={handleChange}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Daily</span>
              </label>
            </div>
          </div>

          {/* Sync Time (shown only when Daily) */}
          {formData.sync_frequency === 'daily' && (
            <div className="mb-4">
              <label htmlFor="sync_time" className="block text-sm font-medium text-gray-700 mb-2">
                Sync Time (Tenant Timezone)
              </label>
              <input
                type="time"
                id="sync_time"
                name="sync_time"
                value={formData.sync_time}
                onChange={handleChange}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
              />
            </div>
          )}

          {/* Include Weekends */}
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="include_weekends"
                checked={formData.include_weekends}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Include Weekends</span>
            </label>
          </div>
        </div>

        {/* Enabled Objects */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Enabled Objects</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {OBJECT_TYPES.map((obj) => (
              <label key={obj.value} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.enabled_objects.includes(obj.value)}
                  onChange={() => handleObjectToggle(obj.value)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">{obj.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Incremental Sync */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Incremental Sync</h3>
          
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="incremental_enabled"
                checked={formData.incremental_enabled}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Enable Incremental Sync</span>
            </label>
          </div>

          {formData.incremental_enabled && (
            <div>
              <label htmlFor="incremental_field" className="block text-sm font-medium text-gray-700 mb-2">
                Incremental Field
              </label>
              <select
                id="incremental_field"
                name="incremental_field"
                value={formData.incremental_field}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
              >
                {INCREMENTAL_FIELDS.map(field => (
                  <option key={field.value} value={field.value}>{field.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Rate Limiting */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Rate Limiting</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="rate_limit_per_minute" className="block text-sm font-medium text-gray-700 mb-2">
                Rate Limit (requests/minute)
              </label>
              <input
                type="number"
                id="rate_limit_per_minute"
                name="rate_limit_per_minute"
                value={formData.rate_limit_per_minute}
                onChange={handleChange}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="max_retries" className="block text-sm font-medium text-gray-700 mb-2">
                Max Retries
              </label>
              <input
                type="number"
                id="max_retries"
                name="max_retries"
                value={formData.max_retries}
                onChange={handleChange}
                min="0"
                max="10"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 text-sm font-medium text-white bg-[#00C6AE] rounded-lg hover:bg-[#00B39E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </form>
    </div>
  );
}
